<script setup lang="ts">
import { ref, computed, reactive, nextTick, onMounted } from 'vue'
import DJPagination from '~/components/ui/pagination/Pagination.vue'
import Rate from '~/components/ui/rate/Rate.vue'
import localDesignerApi from '~/composables/api/localDesignerApi'
import { isEmpty } from '~/helper'
import ItemReview from './ItemReview.vue'

const props = defineProps({
  designer: {
    type: Object,
    default: null,
  },
})
const { $logger } = useNuxtApp()
const { isMobile } = useDevice()
const LIMIT = 5
const LIMIT_PAGINATION = isMobile ? 3 : 5
const DEFAULT_RATING = '5.0'

const reviews = ref<any>(null)
const sectionReviewDesignerRef = ref<HTMLElement | null>(null)
const state = reactive({
  loading: false,
  currentPage: 1,
})
const totalPage = computed(() => {
  if (isEmpty(reviews.value)) return 0
  return Math.ceil(reviews.value.total / LIMIT) || 0
})
const rating = computed(() => {
  if (!props.designer || !props.designer.id) return DEFAULT_RATING
  return props.designer.rating || props.designer.average_rating || DEFAULT_RATING
})
const subTitle = computed(() => {
  if (!reviews) return ''
  const count = reviews.value.total
  return count ? (count === 1 ? `Based on ${count} review` : `Based on ${count} reviews`) : ''
})
const reviewDefault = computed(() => {
  if (!reviews) return []
  return reviews.value.default_review || []
})
const reviewItems = computed(() => {
  const items = reviews.value?.items || []
  return state.currentPage === totalPage.value ? (reviewDefault.value ? items.concat(reviewDefault.value) : items) : items
})

async function getListReviews(page = 1) {
  try {
    state.loading = true
    if (props.designer && props.designer.id) {
      const payload = {
        limit: LIMIT,
        page,
        designer_id: props.designer.id,
      }
      const resp = (await localDesignerApi.getTravellerReviews(payload)).data.value
      if (resp?.success && resp?.data) {
        reviews.value = resp?.data as any
      } else {
        $logger.log(resp?.message, { cause: { function: 'Get list reviews destination' } })
      }
    }
  } catch (error) {
    reviews.value = { items: [], total: 0 }
    $logger.log(error, { cause: { function: 'Get list reviews destination' } })
  } finally {
    state.currentPage = page
    state.loading = false
  }
}
function fetchingData(page: number) {
  handleScroll()
  getListReviews(page)
}
function handleScroll() {
  if (sectionReviewDesignerRef.value && sectionReviewDesignerRef.value.offsetTop) {
    window.scrollTo({
      top: sectionReviewDesignerRef.value.offsetTop - 200,
      behavior: 'smooth',
    })
  }
}
onMounted(() => {
  nextTick(() => getListReviews())
})

defineExpose({
  reviews,
})
</script>

<template>
  <div v-if="reviews && reviews.total" ref="sectionReviewDesignerRef" class="relative" id="section-review-designer">
    <UiLoading class="z-4" :is-active="state.loading" />
    <div class="flex items-center flex-wrap">
      <div class="flex items-center">
        <div class="mr-8 flex items-end">
          <h2 class="heading-2-bd gray-950">{{ rating }}</h2>
          <span class="heading-6-md gray-700 mb-4">/5</span>
        </div>
        <Rate class="mr-8" :rate="rating" />
      </div>
      <span class="text-lg-md mb-text-sm-rg gray-700 mb-wp-100">{{ subTitle }}</span>
    </div>
    <ItemReview v-for="review in reviewItems" :key="review.date" :review="review" />
    <DJPagination
      v-if="totalPage > 1"
      :totalRecord="reviews.total"
      :totalPage="totalPage"
      :limit="LIMIT_PAGINATION"
      v-model:currentPage="state.currentPage"
      @action="fetchingData"
    />
  </div>
</template>

<style lang="scss" scoped></style>
